import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Inputbox from "../../Components/Inputbox";
import DropDownButton, {
  DropDownManuCustomMulti,
} from "../../Components/DropDownButton";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { agencySignup, GetCategory, uploadImage } from "../../../api/helper";
import { ShowToast } from "../../../helpers/ToastService";
import { Store } from "../../../redux";
import {
  setHeaderToken,
  setLoginType,
  setUserDetails,
} from "../../../redux/cookiesReducer";
import { useDispatch } from "react-redux";
import { GoogleMap, useLoadScript, Autocomplete } from "@react-google-maps/api";

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),
  lastName: Yup.string()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),

  phone: Yup.string()
    .required("Phone is required")
    .matches(/^[0-9]+$/, "Phone number must be digits")
    .min(10, "Phone number must be at least 10 digits")
    .max(15, "Phone number cannot exceed 15 digits"),
  password: Yup.string()
    .required("Password is required")
    .min(12, "Password must be at least 12 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*_,]/,
      "Password must contain at least one special character (!, @, #, $, %, ^, &, *, _)"
    )
    .notOneOf(
      ["password", "12345", "qwerty"],
      "Password is too common or easy to guess"
    )
    .test(
      "no-repeated-characters",
      "Password should not contain repeated characters like 'aaaa' or '1111'",
      (value) => {
        return !/(.)\1{3,}/.test(value); // Adjust this to your repeated character tolerance
      }
    )
    .test(
      "no-simple-patterns",
      "Password should not contain common patterns like 'abcd' or '1234'",
      (value) => {
        const patterns = ["abcd", "1234", "qwerty"]; // Add more patterns as needed
        return !patterns.some((pattern) => value.includes(pattern));
      }
    ),

  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),

  companyName: Yup.string()
    .required("Company name is required")
    .min(2, "Company name must be at least 2 characters"),

  address: Yup.object().shape({
    // street: Yup.string().required('Street is required'),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    country: Yup.string().required("Country is required"),
    zipCode: Yup.string()
      .required("Zip code is required")
      .matches(/^[0-9]+$/, "Zip code must be digits")
      .min(5, "Zip code must be at least 5 digits")
      .max(10, "Zip code cannot exceed 10 digits"),
  }),
  NPI2: Yup.string().required("NPI2 is required"),
  DBA: Yup.string().required("DBA is required"),
  EIN: Yup.string().required("EIN is required"),
  categoryId: Yup.array()
    .of(Yup.string().required("A Specialities must be selected"))
    .min(1, "At least one Specialities is required")
    .required("Specialities is required"),
});

const AgencyRegistration = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const dispatch = useDispatch();
  const [autocomplete, setAutocomplete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [saveData, setSaveData] = useState();
  const fileInputRef = useRef(null);
  const [imaeg, setImages] = useState(null);
  const [formSubmit, setFormSubmit] = useState(null);
  const [specialtydata, setspecialtydata] = useState(null);
  const [suggestedPassword, setSuggestedPassword] = useState("");

  useEffect(() => {
    setSuggestedPassword(generateStrongPassword());
  }, []);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyAoF8F2jhZ_6-77oYJi0hgGTbcVh-CCIEE",
    libraries: ["places"],
  });

  const getSpecialty = async () => {
    try {
      const resposne = await GetCategory();
      setspecialtydata(resposne?.data?.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getSpecialty();
  }, []);

  const handlePlaceSelect = async () => {
    const place = autocomplete.getPlace();
    const addressComponents = place.address_components;
    const getAddressComponent = (type) => {
      const component = addressComponents.find((c) => c.types.includes(type));
      return component ? component.long_name : "";
    };
    setValue("address.country", getAddressComponent("country"));
    setValue(
      "address.state",
      getAddressComponent("administrative_area_level_1")
    );
    setValue("address.city", getAddressComponent("locality"));

    const streetNumber = getAddressComponent("street_number");
    const route = getAddressComponent("route");
    const sublocality = getAddressComponent("sublocality");
    const neighborhood = getAddressComponent("neighborhood");
    const premise = getAddressComponent("premise");
    const establishment = getAddressComponent("establishment");

    let fullStreet = "";

    if (establishment) {
      fullStreet = establishment;
    } else if (premise) {
      fullStreet = `${premise}, ${route || sublocality || neighborhood}`;
    } else if (streetNumber && route) {
      fullStreet = `${streetNumber} ${route}`;
    } else if (sublocality) {
      fullStreet = sublocality;
    } else if (neighborhood) {
      fullStreet = neighborhood;
    }
    if (!fullStreet) {
      fullStreet = `${getAddressComponent("locality")}, ${getAddressComponent(
        "administrative_area_level_1"
      )}, ${getAddressComponent("country")}`;
    }
    setValue("address.street", fullStreet);
    setValue("address.zipCode", getAddressComponent("postal_code"));
  };

  const handleChange = (e) => {
    setValue("phone", e);
  };

  const handleCloseModal = async (data) => {
    if (data == "Upload") {
      if (!imagePreview) {
        return ShowToast("Please Select Image", "error");
      }
      saveData.image = imaeg;
    }
    setFormSubmit(data);
    setShowModal(false);
    submitData();
  };

  const onSubmit = async (data) => {
    // console.log(data, '======data')
    setSaveData(data);
    formSubmit ? submitData() : setShowModal(true);
  };

  const submitData = async () => {
    try {
      const resposne = await agencySignup(saveData);
      ShowToast(resposne.data.message, "success");
      Store.dispatch(setHeaderToken(resposne?.data?.data));
      dispatch(setUserDetails(resposne.data.data));
      Store.dispatch(setLoginType("Clinic"));
      navigate("/dashboradScreen");
    } catch (error) {
      console.log(error);
    }
  };

  const clickImageData = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const imagePicker = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("images", file);
      try {
        const response = await uploadImage(formData);
        // console.log(response.data.data[0].URL,'====response')
        setImages(response.data.data[0].URL);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.log(error, "===");
      }
    }
  };

  const handleSelect = (selectedOptions) => {
    console.log("selectedOptions:", selectedOptions);
    let ids = Array.isArray(selectedOptions)
      ? selectedOptions.map((e) => e?._id)
      : [];
    setValue("categoryId", ids);
  };
  const generateStrongPassword = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const number = "0123456789";
    const special = "!@#$%^&*_";
    const all = upper + lower + number + special;

    let password = "";
    password += upper[Math.floor(Math.random() * upper.length)];
    password += lower[Math.floor(Math.random() * lower.length)];
    password += number[Math.floor(Math.random() * number.length)];
    password += special[Math.floor(Math.random() * special.length)];

    for (let i = 4; i < 12; i++) {
      password += all[Math.floor(Math.random() * all.length)];
    }

    return password
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");
  };

  if (!isLoaded) return <div>Loading...</div>;
  return (
    <div className="login-container">
      <div className="left-panel">
        <img
          src={require("../../assest/Images/mindwellnewlogo.png")}
          alt="Mindwell Logo"
          className="logo"
        />
        <div className="slogan">
          Your trusted platform for mental health services.
        </div>
      </div>
      <div className="signup-container">
        <form onSubmit={handleSubmit(onSubmit)} className="signup-form">
          <h2>New Agency Registration</h2>
          <div className="signup-form-content">
            <div className="input-row_two">
              <div className="form_div">
                <Inputbox
                  label="Contact Name"
                  type="text"
                  placeholder="First Name"
                  name="firstName"
                  register={register}
                />
                {errors.firstName && (
                  <div className="errorMessage">{errors.firstName.message}</div>
                )}
              </div>
              <div className="form_div last-name-field">
                <Inputbox
                  type="text"
                  label=""
                  placeholder="Last Name"
                  name="lastName"
                  register={register}
                />
                {errors.lastName && (
                  <div className="errorMessage">{errors.lastName.message}</div>
                )}
              </div>
            </div>

            <div className="input-row_two">
              <div className="form_div">
                <Inputbox
                  label="Email Address"
                  type="email"
                  placeholder="Enter Email"
                  name="email"
                  register={register}
                />
                {errors.email && (
                  <div className="errorMessage">{errors.email.message}</div>
                )}
              </div>
              <div className="form_div_two">
                <Inputbox
                  className="phone-input"
                  label="Phone Number"
                  type="text"
                  placeholder="Enter Number"
                  name="phone"
                  onChange={handleChange}
                />
                {errors.phone && (
                  <div className="errorMessage">{errors.phone.message}</div>
                )}
              </div>
            </div>

            <div className="input-row_two">
              <div className="form_div">
                <Inputbox
                  label="Password"
                  type="password"
                  placeholder="Enter Password"
                  name="password"
                  register={register}
                  isPassword
                />
                {errors.password && (
                  <div className="errorMessage">{errors.password.message}</div>
                )}
                {suggestedPassword && (
                  <div className="suggest-password-box">
                    <span>Suggested: </span>
                    <button
                      type="button"
                      className="use-suggested-btn"
                      onClick={() => {
                        setValue("password", suggestedPassword);
                        setValue("confirmPassword", suggestedPassword);
                        clearErrors(["password", "confirmPassword"]);
                      }}
                    >
                      <div className="use-suggested-btn">
                        {" "}
                        Use suggested password:{" "}
                        <strong>{suggestedPassword}</strong>
                      </div>
                    </button>
                  </div>
                )}
              </div>
              <div className="form_div">
                <Inputbox
                  label="Confirm Password"
                  type="password"
                  placeholder="Enter Confirm Password"
                  name="confirmPassword"
                  register={register}
                  isPassword
                />
                {errors.confirmPassword && (
                  <div className="errorMessage">
                    {errors.confirmPassword.message}
                  </div>
                )}
              </div>
            </div>

            <div className="Company_Details_txt">Company Details :</div>
            <div className="form_div">
              <Inputbox
                label="Company Legal Name"
                type="text"
                placeholder="Enter Company Legal Name"
                name="companyName"
                register={register}
              />
              {errors.companyName && (
                <div className="errorMessage">{errors.companyName.message}</div>
              )}
            </div>
            <div className="spacer" />
            <div className="input-row_two">
              <div className="form_div">
                <DropDownManuCustomMulti
                  label="Specialities"
                  placeholder="Select option"
                  options={specialtydata}
                  name="categoryId"
                  register={register}
                  id="_id"
                  clearErrors={clearErrors}
                  onSelect={handleSelect}
                />
                {errors.categoryId && (
                  <div className="errorMessage">
                    {errors.categoryId.message}
                  </div>
                )}
              </div>
            </div>
            <div className="input-row_two">
              <div className="form_div">
                <Inputbox
                  label="DBA"
                  type="text"
                  placeholder="Enter DBA"
                  name="DBA"
                  register={register}
                />
                {errors.DBA && (
                  <div className="errorMessage">{errors.DBA.message}</div>
                )}
              </div>
              <div className="form_div">
                <Inputbox
                  label="NPI 2"
                  type="text"
                  placeholder="Enter NPI 2"
                  name="NPI2"
                  register={register}
                />
                {errors.NPI2 && (
                  <div className="errorMessage">{errors.NPI2.message}</div>
                )}
              </div>
            </div>

            <div className="form_div">
              <Inputbox
                label="EIN"
                type="text"
                placeholder="Enter EIN"
                name="EIN"
                register={register}
              />
              {errors.EIN && (
                <div className="errorMessage">{errors.EIN.message}</div>
              )}
            </div>

            <div className="input-row_two">
              <div className="form_div">
                <Autocomplete
                  onLoad={(autocompleteInstance) =>
                    setAutocomplete(autocompleteInstance)
                  }
                  onPlaceChanged={handlePlaceSelect}
                >
                  <Inputbox
                    label="Street"
                    type="text"
                    placeholder="Enter Street"
                    name="address.street"
                    register={register}
                  />
                </Autocomplete>
              </div>
              <div className="form_div">
                <Inputbox
                  label="City"
                  type="text"
                  placeholder="Enter City"
                  name="address.city"
                  register={register}
                />
                {errors.address?.city && (
                  <div className="errorMessage">
                    {errors.address.city.message}
                  </div>
                )}
              </div>
            </div>
            <div className="input-row_two">
              <div className="form_div">
                <Inputbox
                  label="State"
                  type="text"
                  placeholder="Enter State"
                  name="address.state"
                  register={register}
                />
                {errors.address?.state && (
                  <div className="errorMessage">
                    {errors.address.state.message}
                  </div>
                )}
              </div>
              <div className="form_div">
                <Inputbox
                  label="Country"
                  type="text"
                  placeholder="Enter Country"
                  name="address.country"
                  register={register}
                />
                {errors.address?.country && (
                  <div className="errorMessage">
                    {errors.address.country.message}
                  </div>
                )}
              </div>
            </div>

            <div className="input-row_two">
              <div className="form_div">
                <Inputbox
                  label="Zip Code"
                  type="text"
                  placeholder="Enter Zip Code"
                  name="address.zipCode"
                  register={register}
                />
                {errors.address?.zipCode && (
                  <div className="errorMessage">
                    {errors.address.zipCode.message}
                  </div>
                )}
              </div>
            </div>

            <button className="login-button" type="submit">
              Sign Up
            </button>
            <p className="login-text">
              Already have an Account?{" "}
              <a onClick={() => navigate("/")} className="login-link">
                Log In
              </a>
            </p>
          </div>
        </form>
      </div>
      {showModal && (
        <div className="modal_div">
          <div className="logout-modal">
            <div className="logout_txt">Upload Profile Picture</div>
            <div className="image-upload-container">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="image-preview"
                />
              ) : (
                <img
                  src={require("../../assest/Images/Cameraprofile.png")}
                  alt="Upload"
                  className="upload-icon"
                />
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={imagePicker}
                style={{ display: "none" }}
              />
            </div>
            <button
              className="confirm-button"
              onClick={() => handleCloseModal("Upload")}
            >
              Upload
            </button>
            <div className="skip-link" onClick={() => handleCloseModal("Skip")}>
              Skip for now
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyRegistration;
